import { Button, buttonVariants, StatusScreen } from '@servicetim/ui/components';
import { useSession } from '@servicetim/ui/lib';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

/**
 * Sign-out route. The shell's "Sign out" button navigates here; this page owns the actual sign-out
 * so the AuthGate (which would immediately restart sign-in) is already unmounted when the session
 * disappears. It is also `config.cognito.postLogoutRedirectUri`, so Cognito lands here afterwards.
 * The query cache is cleared first so no tenant data survives into the next session (unit 10).
 */
export function SignedOutPage() {
  const { authEnabled, status, signOut, signIn } = useSession();
  const queryClient = useQueryClient();
  const started = useRef(false);

  useEffect(() => {
    if (!authEnabled || status !== 'authenticated' || started.current) return;
    started.current = true;
    queryClient.clear();
    void signOut();
  }, [authEnabled, status, signOut, queryClient]);

  if (!authEnabled) {
    return (
      <StatusScreen
        tone="info"
        title="Nothing to sign out of"
        description="Authentication is disabled in local development mode."
        action={
          <Link to="/" className={buttonVariants({ variant: 'outline' })}>
            Back to the app
          </Link>
        }
      />
    );
  }

  if (status === 'authenticated' || status === 'loading') {
    return <StatusScreen tone="loading" title="Signing you out…" />;
  }

  return (
    <StatusScreen
      tone="info"
      title="You are signed out"
      description="Your session on this device has been cleared."
      action={<Button onClick={() => void signIn('/')}>Sign in again</Button>}
    />
  );
}
